// Email mailbox management + per-mailbox HTML signature for compose.

import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Mail, Loader2, AlertCircle, CheckCircle2, Trash2, Plus, Save } from "lucide-react";
import { Button, IconButton } from "../../atoms";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import EmailComposeEditor from "../../molecules/EmailComposeEditor";
import {
  useEmailAccounts, useConnectGmail, useDisconnectEmailAccount,
  useEmailSyncSetup, useRenewGmailWatches, useUpdateEmailSignature,
} from "../../hooks/useEmails";

function SyncModeBanner({ setup, isAdmin, onRenew, renewing }) {
  if (!setup) return null;

  const pollMin = Math.round((setup.pollIntervalMs || 0) / 60000);
  const bf = setup.backfill;
  // Only claim a poll backup when the poll is actually running — this label
  // used to say "+ poll backup" purely because push was configured, while the
  // poll job itself was switched off server-side.
  const mode = setup.pushReady
    ? (setup.pollEnabled
      ? `Instant sync (Gmail push) + poll backup every ${pollMin} min`
      : "Instant sync (Gmail push) — no poll backup")
    : setup.pubSubConfigured
      ? "Push partially configured — check server env"
      : `Poll-only (every ${pollMin} min)`;

  return (
    <div style={{
      marginBottom: 14,
      padding: "12px 14px",
      borderRadius: 8,
      background: setup.pushReady ? "var(--crm-success-soft, #ecfdf5)" : "var(--crm-primary-soft)",
      border: "1px solid var(--color-border-subtle)",
      fontSize: 12,
      lineHeight: 1.5,
      color: "var(--color-text-secondary)",
    }}
    >
      <div style={{ fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 4 }}>
        Sync mode: {mode}
      </div>
      <div>
        Org mailboxes connected: <strong>{setup.connectedMailboxes || 0}</strong>
        {!setup.pubSubConfigured && (
          <span> — new mail syncs on the poll schedule until Gmail Pub/Sub is configured on the server.</span>
        )}
      </div>
      {bf && bf.total > 0 && (
        <div style={{ marginTop: 6 }}>
          {bf.pending > 0 ? (
            <>
              Indexing email history: <strong>{bf.done}</strong> of <strong>{bf.total}</strong> leads
              {" "}— {bf.pending} still queued, runs automatically in the background.
            </>
          ) : (
            <>Email history indexed for all <strong>{bf.total}</strong> leads with an address.</>
          )}
          {bf.stuck > 0 && (
            <span style={{ color: "var(--crm-warning, #B45309)" }}>
              {" "}{bf.stuck} lead{bf.stuck === 1 ? "" : "s"} could not be indexed.
            </span>
          )}
        </div>
      )}
      {isAdmin && setup.pubSubConfigured && !setup.pushReady && (
        <div style={{ marginTop: 6, color: "var(--crm-warning, #B45309)" }}>
          Push webhook or auth env missing on server — see boot logs / README.
        </div>
      )}
      {isAdmin && setup.pubSubConfigured && (
        <div style={{ marginTop: 10 }}>
          <Button variant="secondary" size="sm" onClick={onRenew} disabled={renewing}>
            {renewing ? "Renewing…" : "Renew Gmail push watches"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ProviderBadge({ provider }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 8px", borderRadius: 999,
      fontSize: 10, fontWeight: 600, background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
    }}>
      {provider}
    </span>
  );
}

function StatusPill({ status }) {
  if (status === "active") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: "var(--crm-success)" }}>
        <CheckCircle2 size={12} /> Connected
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color: "var(--crm-warning, #B45309)" }}>
      <AlertCircle size={12} /> Reauth required
    </span>
  );
}

function SignatureEditor({ account }) {
  const saveSig = useUpdateEmailSignature();
  const [html, setHtml] = useState(account.signatureHtml || "");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setHtml(account.signatureHtml || "");
  }, [account.signatureHtml, account._id]);

  const onSave = async () => {
    try {
      await saveSig.mutateAsync({ accountId: account._id, signatureHtml: html });
      toast.success("Signature saved");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Save failed");
    }
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--color-border-subtle)" }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          border: "none", background: "transparent", cursor: "pointer",
          fontSize: 12, fontWeight: 600, color: "var(--crm-primary)", padding: 0,
        }}
      >
        {expanded ? "Hide signature" : "Edit email signature"}
      </button>
      {expanded && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 11, color: "var(--color-text-secondary)", margin: "0 0 8px", lineHeight: 1.5 }}>
            Use <strong>Layout</strong> for a logo + contact block, then upload your logo (auto-resized to 200px / ~120 KB).
          </p>
          <EmailComposeEditor
            value={html}
            onChange={setHtml}
            placeholder="Your name, title, phone…"
            allowImages
            minHeight={160}
          />
          <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
            <Button size="sm" onClick={onSave} disabled={saveSig.isPending}>
              {saveSig.isPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              <span style={{ marginLeft: 6 }}>Save signature</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmailAccountsSection({ isAdmin = false }) {
  const { data: accounts = [], isLoading } = useEmailAccounts();
  const { data: syncSetup } = useEmailSyncSetup();
  const connectGmail = useConnectGmail();
  const disconnect = useDisconnectEmailAccount();
  const renewWatches = useRenewGmailWatches();
  const [confirmAccount, setConfirmAccount] = useState(null);

  const onConnectGmail = async () => {
    try {
      const { authUrl } = await connectGmail.mutateAsync();
      window.location.href = authUrl;
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to start Gmail connect");
    }
  };

  const onDisconnect = (accountId, email) => setConfirmAccount({ accountId, email });

  const performDisconnect = async () => {
    if (!confirmAccount) return;
    try {
      await disconnect.mutateAsync(confirmAccount.accountId);
      setConfirmAccount(null);
      toast.success("Disconnected");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const onRenewWatches = async () => {
    try {
      const r = await renewWatches.mutateAsync();
      if (r.skipped) {
        toast.error("Pub/Sub not configured on server");
        return;
      }
      toast.success(`Renewed ${r.renewed}/${r.total} mailbox watches`);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Renew failed");
    }
  };

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 20 }}>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--color-text-primary)" }}>Email</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
            Connect Gmail to read and send mail from the Inbox tab. Set a signature per mailbox — it is appended when you compose.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Button variant="ghost" onClick={onConnectGmail} disabled={connectGmail.isPending}>
            {connectGmail.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            <span style={{ marginLeft: 6 }}>Connect Gmail</span>
          </Button>
        </div>
      </div>

      <SyncModeBanner
        setup={syncSetup}
        isAdmin={isAdmin}
        onRenew={onRenewWatches}
        renewing={renewWatches.isPending}
      />

      {isLoading && (
        <div style={{ padding: 16, display: "flex", alignItems: "center", gap: 8, color: "var(--color-text-secondary)", fontSize: 13 }}>
          <Loader2 size={14} className="animate-spin" /> Loading mailboxes…
        </div>
      )}

      {!isLoading && accounts.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", border: "1px dashed var(--color-border-subtle)", borderRadius: 8, color: "var(--color-text-secondary)" }}>
          <Mail size={24} style={{ opacity: 0.4, marginBottom: 6 }} />
          <div style={{ fontSize: 13 }}>No mailboxes connected yet.</div>
        </div>
      )}

      {!isLoading && accounts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {accounts.map((a) => (
            <div key={a._id} style={{
              border: "1px solid var(--color-border-subtle)", borderRadius: 8,
              padding: "12px 14px", background: "var(--color-bg-base)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Mail size={16} style={{ color: "var(--color-text-secondary)" }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.email}
                    </span>
                    <ProviderBadge provider={a.provider} />
                  </div>
                  <div style={{ marginTop: 2 }}>
                    <StatusPill status={a.status} />
                  </div>
                </div>
                <IconButton aria-label="Disconnect" onClick={() => onDisconnect(a._id, a.email)} disabled={disconnect.isPending}>
                  <Trash2 size={14} />
                </IconButton>
              </div>
              {a.status === "active" && <SignatureEditor account={a} />}
            </div>
          ))}
        </div>
      )}
      <ConfirmDeleteModal
        open={!!confirmAccount}
        onClose={() => setConfirmAccount(null)}
        onConfirm={performDisconnect}
        title="Disconnect this mailbox?"
        message={confirmAccount ? `${confirmAccount.email} will be disconnected. You can reconnect anytime.` : ""}
        confirmLabel="Disconnect"
        isPending={disconnect.isPending}
      />
    </section>
  );
}
